import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, UserIcon } from '@heroicons/react/24/outline';
import {
  getPayrollImportById,
  getPayrollImportRecord,
  type PayrollImportDocument,
  type PayrollImportRecord,
} from '../services';
import { useToast } from '@/hooks/useToast';
import { SubPageHeader } from '@/components/shared/SubPageHeader/SubPageHeader';

const formatCurrency = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return '—';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('pt-BR');
};

const formatCpf = (cpf?: string | null): string => {
  if (!cpf) return '—';
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const formatPdType = (value?: string | null) => {
  switch (value) {
    case 'earning':
      return 'Provento';
    case 'deduction':
      return 'Dedução';
    default:
      return '—';
  }
};

const formatNature = (value?: string | null) => {
  switch (value) {
    case 'earning':
      return 'Provento';
    case 'deduction':
      return 'Dedução';
    case 'tax':
      return 'Tributo';
    case 'employer_charge':
      return 'Encargo empregador';
    case 'informative':
      return 'Informativo';
    default:
      return '—';
  }
};

const formatInteger = (value?: number | null) => {
  if (value === undefined || value === null || Number.isNaN(value)) return '—';
  return Math.round(value).toString();
};

export function PayrollImportCollaboratorDetailsPage() {
  const { importId, collaboratorId } = useParams<{ importId: string; collaboratorId: string }>();
  const navigate = useNavigate();
  const { error: showError } = useToast();

  const [importData, setImportData] = useState<PayrollImportDocument | null>(null);
  const [record, setRecord] = useState<PayrollImportRecord | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      if (!importId || !collaboratorId) return;
      try {
        setLoading(true);
        const [importDoc, recordDoc] = await Promise.all([
          getPayrollImportById(importId),
          getPayrollImportRecord(importId, collaboratorId),
        ]);

        if (!importDoc) {
          showError('Importação não encontrada.');
          navigate('/expenses/payroll', { replace: true });
          return;
        }
        if (!recordDoc) {
          showError('Registro do colaborador não encontrado.');
          navigate(`/expenses/payroll/${importId}`, { replace: true });
          return;
        }

        setImportData(importDoc);
        setRecord(recordDoc);
      } catch (err) {
        console.error('[PayrollImportCollaboratorDetailsPage] Erro ao carregar registro', err);
        showError('Não foi possível carregar os detalhes do colaborador.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collaboratorId, importId]);

  const totals = useMemo(() => {
    if (!record) return { earnings: 0, deductions: 0 };
    return record.payrollRecord.items.reduce(
      (acc, item) => {
        if (item.pdType === 'earning') acc.earnings += item.amount ?? 0;
        if (item.pdType === 'deduction') acc.deductions += item.amount ?? 0;
        return acc;
      },
      { earnings: 0, deductions: 0 },
    );
  }, [record]);

  const earningsItems = useMemo(
    () => record?.payrollRecord.items.filter((item) => item.pdType === 'earning') ?? [],
    [record],
  );
  const deductionItems = useMemo(
    () => record?.payrollRecord.items.filter((item) => item.pdType === 'deduction') ?? [],
    [record],
  );

  if (loading) {
    return <div className="text-sm text-gray-600">Carregando colaborador...</div>;
  }

  if (!record || !importData) {
    return <div className="text-sm text-gray-600">Registro não encontrado.</div>;
  }

  return (
    <div className="space-y-6">
      <SubPageHeader
        backTo={`/expenses/payroll/${importId}`}
        icon={<UserIcon className="h-5 w-5" aria-hidden />}
        badge="Colaborador"
        title={record.collaboratorName}
        description={`Competência: ${importData.competence ?? '—'}`}
        actions={
          <div className="flex flex-col items-start text-xs text-gray-500 sm:items-end">
            <span>Import ID: {importId}</span>
            <span>Colaborador ID: {record.collaboratorId}</span>
            {record.payrollEmployeeId ? <span>Folha ID: {record.payrollEmployeeId}</span> : null}
          </div>
        }
      />

      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Dados do registro</h2>
          <p className="text-sm text-gray-600">Informações capturadas do PDF para este colaborador.</p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2 text-sm">
            {[
              ['CPF', formatCpf(record.payrollRecord.cpf)],
              ['ID Folha', record.payrollRecord.employeeId],
              ['Admissão', formatDate(record.payrollRecord.admissionDate)],
              ['Horas mensais', record.payrollRecord.monthlyHours ?? '—'],
              ['Cargo', record.payrollRecord.position ?? '—'],
              ['Tipo vínculo', record.payrollRecord.employmentType ?? '—'],
            ].map(([label, value]) => (
              <div key={label as string} className="flex items-center justify-between">
                <span className="text-gray-600">{label}</span>
                <span className="font-semibold text-gray-900">{value as any}</span>
              </div>
            ))}
          </div>
          <div className="space-y-2 text-sm">
            {[
              ['Centro de custo', record.payrollRecord.costCenter ?? '—'],
              ['Departamento', record.payrollRecord.department?.name ?? record.payrollRecord.departmentCode ?? '—'],
              ['Unidade', record.payrollRecord.branch ?? '—'],
              ['CBO', record.payrollRecord.cbo ?? '—'],
              ['Situação', record.payrollRecord.status ?? '—'],
              ['Salário (base folha)', formatCurrency(record.payrollRecord.baseSalary)],
            ].map(([label, value]) => (
              <div key={label as string} className="flex items-center justify-between">
                <span className="text-gray-600">{label}</span>
                <span className="font-semibold text-gray-900">{value as any}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2 text-sm">
            {[
              ['Deduções', record.payrollRecord.totals?.deductions],
              ['ND', record.payrollRecord.totals?.nd, 'int'],
              ['NF', record.payrollRecord.totals?.nf, 'int'],
              ['Base INSS', record.payrollRecord.totals?.baseInss],
              ['Excedente INSS', record.payrollRecord.totals?.excedenteInss],
            ].map(([label, value, kind]) => (
              <div key={label as string} className="flex items-center justify-between">
                <span className="text-gray-600">{label}</span>
                <span className="font-semibold text-gray-900">
                  {kind === 'int' ? formatInteger(value as number) : formatCurrency(value as number)}
                </span>
              </div>
            ))}
          </div>
          <div className="space-y-2 text-sm">
            {[
              ['Base FGTS', record.payrollRecord.totals?.baseFgts],
              ['Valor FGTS', record.payrollRecord.totals?.fgtsValue],
              ['Base IRRF', record.payrollRecord.totals?.baseIrrf],
              ['Informativa', record.payrollRecord.totals?.informative],
              ['Líquido', record.payrollRecord.totals?.netPay],
            ].map(([label, value]) => (
              <div key={label as string} className="flex items-center justify-between">
                <span className="text-gray-600">{label}</span>
                <span className="font-semibold text-gray-900">{formatCurrency(value as number)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm space-y-3">
          <div>
            <div className="text-xs font-semibold uppercase text-gray-500">Proventos</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">{formatCurrency(totals.earnings)}</div>
          </div>
          <div className="overflow-hidden rounded-md border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Código</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Descrição</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Natureza</th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-700">Valor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {earningsItems.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-3 text-center text-gray-500">
                      Nenhum provento listado.
                    </td>
                  </tr>
                ) : (
                  earningsItems.map((item) => (
                    <tr key={`${item.code}-${item.description}`}>
                      <td className="px-3 py-2 text-gray-900">{item.code}</td>
                      <td className="px-3 py-2 text-gray-700">{item.description}</td>
                      <td className="px-3 py-2 text-gray-700">{formatNature(item.nature)}</td>
                      <td className="px-3 py-2 text-right text-gray-900">{formatCurrency(item.amount)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm space-y-3">
          <div>
            <div className="text-xs font-semibold uppercase text-gray-500">Deduções</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900">{formatCurrency(totals.deductions)}</div>
          </div>
          <div className="overflow-hidden rounded-md border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Código</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Descrição</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Natureza</th>
                  <th className="px-3 py-2 text-right font-semibold text-gray-700">Valor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {deductionItems.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-3 text-center text-gray-500">
                      Nenhuma dedução listada.
                    </td>
                  </tr>
                ) : (
                  deductionItems.map((item) => (
                    <tr key={`${item.code}-${item.description}`}>
                      <td className="px-3 py-2 text-gray-900">{item.code}</td>
                      <td className="px-3 py-2 text-gray-700">{item.description}</td>
                      <td className="px-3 py-2 text-gray-700">{formatNature(item.nature)}</td>
                      <td className="px-3 py-2 text-right text-gray-900">{formatCurrency(item.amount)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="text-sm text-gray-600">
        <Link
          to={`/expenses/payroll/${importId}`}
          className="text-primary-700 underline underline-offset-2 hover:text-primary-800"
        >
          Voltar para a importação
        </Link>
      </div>
    </div>
  );
}
